import { useMemo, useState, useCallback } from 'react'
import { FirmRelationshipsData } from '../../../dtos/applicationData'
import { useApplication } from '../../../context/application'
import {
  ApplicationBodyContainer,
  ApplicationBodyContent,
  DefaultTitle,
  FormContainer,
  FormItemContainer,
  OptionButtonsContainer
} from '../../../components/application/defaultComponents'
import { FormInputErrorMessage } from 'src/components/application/formComponents/styles'
import ApplicationPrebuiltFooter from '../../../components/application/PrebuiltBodyFooter'
import StepBullet from '../../../components/basic/StepBullet'
import OptionButton from '../../../components/basic/OptionButton'
import { ValidationError } from 'yup'
import { FirmRelationshipsSchema } from 'src/yup/NewAppSchema'
import { ValidationErrorMessage } from 'src/components/basic/ValidationErrorMessage/ValidationErrorMessage'

const FirmRelationships = () => {
  const { application } = useApplication()
  const [relationships, setRelationships] = useState<FirmRelationshipsData>(
    () => !!application.firmSection?.relationships ? { ...application.firmSection.relationships } : {}
  )
  const [errorStates, setErrorStates] = useState(false)

  const validatePage = useCallback(() => {
    try {
      FirmRelationshipsSchema.validateSync(relationships, { abortEarly: false })
      return []
    } catch(err) {
      const { errors } = err as ValidationError
      
      return errors
    }
  }, [relationships])

  const validateSteps = useMemo(() => {
    const incorrectSections = validatePage()
    
    let steps: {[key: string]: boolean} = {}
    
    for (let stepNumber = 1; stepNumber <= 10; stepNumber++) {
      steps[`step${stepNumber}`] = !incorrectSections.includes(`step${stepNumber}`)
    }

    return steps
  }, [validatePage])

  const handleScrollToFirstErrorState = () => {
    const incorrectSections = validatePage()
    
    if(incorrectSections.length > 0) {
      document.getElementById(incorrectSections[0])?.scrollIntoView({ behavior: 'smooth' })
      setErrorStates(true)
    }
  }

  const updatedApplicationData = () => ({ 
    ...application, 
    currentPage: window.location.pathname,
    firmSection: {...application.firmSection, relationships: relationships} })

  const goNextRoute = () => {
    if (relationships.hasSuedClientForFees)
      return '/firm/loss-prevention'
    return '/attorneys/main'
  }

  const goBackRoute = () => {
    const sec1 = application.firmSection?.practiceAreas?.find(a => a.area === 'Securities Municipal Bonds - Tax Exempt')
    const sec2 = application.firmSection?.practiceAreas?.find(a => a.area === 'Securities - Other Types Of Securities Practice')

    if((sec1 || sec2) ){
      return '/firm/securities-law'
    }

    const realEstate = application.firmSection?.practiceAreas?.find(a => a.area === 'Real Estate')
    if (realEstate !== undefined && realEstate.types !== undefined && realEstate.types.length > 0) {
      return '/firm/real-estate'
    }
    return '/firm/percentages'
  }

  return (
    <ApplicationBodyContainer>
      <ApplicationBodyContent width='75%'>
        <DefaultTitle>Tell us more about specific relationships or services of your firm or members of your firm.</DefaultTitle>
        <FormContainer maxWidth='760px' style={{ marginTop: '90px' }}>
          <FormItemContainer maxWidth='100%'>
            <StepBullet id="step1" isComplete={validateSteps.step1}>1</StepBullet>
            <OptionButtonsContainer labelSize='big'>
              <span>Has the Firm ever sued a client for fees?</span>
              <div>
                <OptionButton
                  checked={relationships?.hasSuedClientForFees === true}
                  onChange={() => setRelationships({ ...relationships, hasSuedClientForFees: true })}
                  isIncorrect={!validateSteps.step1 && errorStates}
                  errorCode='EC199'
                >
                  Yes
                </OptionButton>
                <OptionButton
                  checked={relationships?.hasSuedClientForFees === false}
                  onChange={() => setRelationships({ ...relationships, hasSuedClientForFees: false })}
                  isIncorrect={!validateSteps.step1 && errorStates}
                  errorCode='EC199'
                >
                  No
                </OptionButton>
              </div>
              <ValidationErrorMessage errorCode='EC199' />
              {(!validateSteps.step1 && errorStates) ? (
                <FormInputErrorMessage>
                  This is a required field
                </FormInputErrorMessage>
              ) : null}
            </OptionButtonsContainer>
          </FormItemContainer>
        </FormContainer>
      </ApplicationBodyContent>
      <ApplicationPrebuiltFooter
        goBackRoute={goBackRoute()}
        isFormValid={Object.keys(validateSteps).every((key) => !!validateSteps[key])}
        nextRoute={goNextRoute()}
        updatedApplication={updatedApplicationData()}
        beforeSaveContinueClick={handleScrollToFirstErrorState}
      />
    </ApplicationBodyContainer>
  )
}

export default FirmRelationships